import React from 'react';
import styled from 'styled-components';
import Navbar from '../components/navbar';
import Footer from '../components/footer';
import {
  FaLock,
  FaRocket,
  FaServer,
  FaHeadset
} from 'react-icons/fa';

/* ---------------------------------
   1) WHOLE-PAGE WRAPPER BACKGROUND
   --------------------------------- */
const PageWrapper = styled.div`
  /* Make sure there are no extra margins/padding */
  margin: 0;
  padding: 0;
  margin-top: -87px;

  /* A full-page linear gradient background */
  min-height: 100vh;
  background: linear-gradient(135deg, #2e3244 0%, #1a1a1a 100%);
  color: white;
`;

/* ----------------------------------
   2) HERO SECTION (RESTORED STYLES)
   ----------------------------------
   Remove margin-top: -25px so it sits flush at the top
*/
const HeroContainer = styled.div`
  position: relative;
  min-height: 80vh; /* Enough to look full-screen-ish */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem 1.5rem;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 80vw;
    height: 80vw;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      circle,
      rgba(52, 152, 219, 0.15) 0%,
      transparent 70%
    );
    z-index: 0; /* behind the hero content */
  }
`;

/* Hero image, title, subtitle, button */
const HeroImage = styled.img`
  max-width: 400px;
  width: 100%;
  margin-bottom: 2rem;
  animation: fadeIn 1.5s ease-in-out;
  z-index: 1; /* ensure above radial */

  @media (max-width: 768px) {
    max-width: 300px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
  background: linear-gradient(90deg, #3498db, #85d2f6);
  -webkit-background-clip: text;
  color: transparent;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: #c1c3d7;
  margin-bottom: 2rem;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const HeroButton = styled.button`
  background-color: #3498db;
  color: #fff;
  border: none;
  padding: 1rem 2.5rem;
  font-size: 1.2rem;
  border-radius: 5px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: transform 0.3s, background-color 0.3s;
  z-index: 1;

  &:hover {
    transform: scale(1.05);
    background-color: #1d73c8;
  }

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: left 0.5s;
  }

  &:hover::after {
    left: 100%;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 2rem;
    font-size: 1rem;
  }
`;

/* --------------------------------
   3) REUSABLE RADIAL SECTION WRAPPER
   -------------------------------- */
const RadialSection = styled.section`
  position: relative;
  padding: 4rem 1.5rem;
  text-align: center;
  z-index: 1;

  &::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    width: 80vw;
    height: 80vw;
    transform: translate(-50%, -50%);
    background: radial-gradient(
      circle,
      rgba(52, 152, 219, 0.15) 0%,
      transparent 70%
    );
    z-index: 0;
  }

  /* Ensure actual content is above the radial background */
  > * {
    position: relative;
    z-index: 1;
  }
`;

/* --------------------------
   4) FEATURES SECTION STYLES
   -------------------------- */
const FeaturesGrid = styled.div`
  max-width: 1200px;
  margin: 2rem auto 0 auto;
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
`;

const FeatureCard = styled.div`
  background: rgba(255, 255, 255, 0.05); 
  padding: 2rem;
  border-radius: 8px;
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-4px);
  }
`;

const FeatureIconWrapper = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: #3498db;
`;

const FeatureHeading = styled.h3`
  font-size: 1.4rem;
  margin-bottom: 0.5rem;
`;

const FeatureText = styled.p`
  font-size: 1rem;
  color: #c1c3d7;
`;

/* -------------------------
   5) PRICING SECTION STYLES
   ------------------------- */
const PricingGrid = styled.div`
  max-width: 1200px;
  margin: 2rem auto 0 auto;
  display: grid;
  gap: 2rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
`;

const PricingCard = styled.div`
  background: rgba(255, 255, 255, 0.05); 
  padding: 2rem;
  border-radius: 8px;
  transition: transform 0.3s;

  &:hover {
    transform: translateY(-4px);
  }
`;

const PlanName = styled.h3`
  font-size: 1.6rem;
  margin-bottom: 1rem;
`;

const PlanPrice = styled.div`
  font-size: 2rem;
  font-weight: bold;
  margin-bottom: 1rem;
`;

const PlanFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 1.5rem;
`;

const PlanFeature = styled.li`
  margin: 0.5rem 0;
  color: #c1c3d7;
`;

/* Reuse HeroButton style for sign-up buttons */
const PlanButton = styled(HeroButton)`
  margin-top: 1rem;
`;

/* -----------------------------
   6) TESTIMONIALS SECTION STYLE
   ----------------------------- */
const TestimonialsContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto 0 auto;
  display: grid;
  gap: 2rem;
  grid-template-columns: 1fr;
`;

const TestimonialCard = styled.div`
  background: rgba(255, 255, 255, 0.05); 
  padding: 2rem;
  border-radius: 8px;
`;

const TestimonialText = styled.p`
  color: #c1c3d7;
  font-style: italic;
  margin-bottom: 1rem;
`;

const TestimonialAuthor = styled.p`
  font-weight: bold;
  color: #fff;
`;

/* ---------------------
   7) FAQ SECTION STYLES
   --------------------- */
const FAQContainer = styled.div`
  max-width: 900px;
  margin: 2rem auto 0 auto;
  text-align: left;
`;

const Question = styled.h3`
  font-size: 1.2rem;
  margin-top: 1.5rem;
  margin-bottom: 0.5rem;
  color: #3498db;
`;

const Answer = styled.p`
  color: #c1c3d7;
  line-height: 1.5;
`;

/* ------------------------
   8) CTA SECTION STYLES
   ------------------------ */
const CTATitle = styled.h2`
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #fff;
`;

const CTASubtitle = styled.p`
  color: #c1c3d7;
  margin-bottom: 2rem;
`;

/* ------------
   HOME PAGE
   ------------ */
const HomePage = () => {
  return (
    <PageWrapper>

      {/* HERO SECTION */}
      <HeroContainer>
        <HeroImage src="assets/logotest.png" alt="Hero Logo" />
        <HeroTitle>Reliable Hosting for Everyone</HeroTitle>
        <HeroSubtitle>Fast, secure, and affordable hosting solutions for your needs.</HeroSubtitle>
        <HeroButton>Get Started</HeroButton>
      </HeroContainer>

      {/* FEATURES SECTION */}
      <RadialSection>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Key Features</h2>
        <FeaturesGrid>
          <FeatureCard>
            <FeatureIconWrapper>
              <FaRocket />
            </FeatureIconWrapper>
            <FeatureHeading>Lightning Fast</FeatureHeading>
            <FeatureText>
              Modern infrastructure and caching deliver blazing speeds.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIconWrapper>
              <FaLock />
            </FeatureIconWrapper>
            <FeatureHeading>Secure & Reliable</FeatureHeading>
            <FeatureText>
              Your data is protected with top-notch security and daily backups.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIconWrapper>
              <FaServer />
            </FeatureIconWrapper>
            <FeatureHeading>99.99% Uptime</FeatureHeading>
            <FeatureText>
              We guarantee continuous operation so your site is always up.
            </FeatureText>
          </FeatureCard>

          <FeatureCard>
            <FeatureIconWrapper>
              <FaHeadset />
            </FeatureIconWrapper>
            <FeatureHeading>24/7 Support</FeatureHeading>
            <FeatureText>
              Our expert team is here around-the-clock to help with any issues.
            </FeatureText>
          </FeatureCard>
        </FeaturesGrid>
      </RadialSection>

      {/* PRICING SECTION */}
      <RadialSection>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>Choose Your Plan</h2>
        <p style={{ color: '#c1c3d7', marginBottom: '3rem' }}>
          Find a plan that fits your needs. No hidden fees.
        </p>

        <PricingGrid>
          <PricingCard>
            <PlanName>Basic</PlanName>
            <PlanPrice>$5/mo</PlanPrice>
            <PlanFeatures>
              <PlanFeature>1 Website</PlanFeature>
              <PlanFeature>10GB Storage</PlanFeature>
              <PlanFeature>Unlimited Bandwidth</PlanFeature>
              <PlanFeature>24/7 Support</PlanFeature>
            </PlanFeatures>
            <PlanButton>Sign Up</PlanButton>
          </PricingCard>

          <PricingCard>
            <PlanName>Pro</PlanName>
            <PlanPrice>$15/mo</PlanPrice>
            <PlanFeatures>
              <PlanFeature>10 Websites</PlanFeature>
              <PlanFeature>50GB Storage</PlanFeature>
              <PlanFeature>Unlimited Bandwidth</PlanFeature>
              <PlanFeature>24/7 Support</PlanFeature>
              <PlanFeature>Free Domain</PlanFeature>
            </PlanFeatures>
            <PlanButton>Sign Up</PlanButton>
          </PricingCard>

          <PricingCard>
            <PlanName>Enterprise</PlanName>
            <PlanPrice>$30/mo</PlanPrice>
            <PlanFeatures>
              <PlanFeature>Unlimited Websites</PlanFeature>
              <PlanFeature>200GB Storage</PlanFeature>
              <PlanFeature>Unlimited Bandwidth</PlanFeature>
              <PlanFeature>Premium Support</PlanFeature>
              <PlanFeature>Free SSL Certificates</PlanFeature>
            </PlanFeatures>
            <PlanButton>Sign Up</PlanButton>
          </PricingCard>
        </PricingGrid>
      </RadialSection>

      {/* TESTIMONIALS SECTION */}
      <RadialSection>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>What Our Customers Say</h2>
        <TestimonialsContainer>
          <TestimonialCard>
            <TestimonialText>
              “I switched to this service a few months ago, and my site’s speed
              has never been better. Fantastic support team!”
            </TestimonialText>
            <TestimonialAuthor>- Sarah, Startup Founder</TestimonialAuthor>
          </TestimonialCard>

          <TestimonialCard>
            <TestimonialText>
              “Reliable uptime and stellar customer service. Highly recommend to
              anyone looking for a solid hosting partner.”
            </TestimonialText>
            <TestimonialAuthor>- Mark, Web Developer</TestimonialAuthor>
          </TestimonialCard>
        </TestimonialsContainer>
      </RadialSection>

      {/* FAQ SECTION */}
      <RadialSection>
        <h2 style={{ fontSize: '2.5rem', marginBottom: '2rem' }}>Frequently Asked Questions</h2>
        <FAQContainer>
          <Question>1. How does the free domain work?</Question>
          <Answer>
            With our Pro and Enterprise plans, we’ll register a domain for you
            free of charge for the first year.
          </Answer>

          <Question>2. Do you offer refunds?</Question>
          <Answer>
            We provide a 30-day money-back guarantee if you’re not fully
            satisfied with our service.
          </Answer>

          <Question>3. Can I upgrade my plan later?</Question>
          <Answer>
            Absolutely! You can switch to a higher plan at any time. Just reach
            out to our support team, and we'll handle the rest.
          </Answer>
        </FAQContainer>
      </RadialSection>

      {/* FINAL CTA SECTION */}
      <RadialSection>
        <CTATitle>Ready to Get Started?</CTATitle>
        <CTASubtitle>
          Join thousands of happy customers using our services.
        </CTASubtitle>
        <HeroButton>Choose a Plan</HeroButton>
      </RadialSection>

    </PageWrapper>
  );
};

export default HomePage;
